/**
 * ─── Payroll Engine ──────────────────────────────────────────
 * Orchestrates payroll computation per pay period.
 * Reads attendance, computes earnings/deductions, generates payslips.
 *
 * DDD Application Service — coordinates between:
 *  - ContributionService (SSS, PhilHealth, Pag-IBIG)
 *  - TaxService (BIR withholding tax)
 *  - Attendance records
 *  - Employee master data
 */

import type {
  Employee, AttendanceRecord, Payslip,
  PayrollPeriod, PayrollConfig, PayFrequency,
} from './hris.schema'
import { DEFAULT_PAYROLL_CONFIG } from './hris.schema'
import { ContributionService } from './contribution.service'
import { TaxService } from './tax.service'

export interface PayrollRunInput {
  period: Pick<PayrollPeriod, 'periodStart' | 'periodEnd' | 'payFrequency' | 'fiscalYear' | 'fiscalMonth'>
  employees: Employee[]
  attendanceRecords: AttendanceRecord[]   // All attendance for the period
  loansByEmployee: Map<string, { sssLoan: number; pagibigLoan: number; cashAdvances: number }>
  config?: PayrollConfig
}

export interface PayrollJournalEntry {
  accountName: string
  debit: number
  credit: number
}

export interface PayrollRunResult {
  payslips: Payslip[]
  summary: {
    totalGrossPay: number
    totalDeductions: number
    totalNetPay: number
    employeeCount: number
  }
  journalEntries: PayrollJournalEntry[]
}

export class PayrollEngine {
  private config: PayrollConfig

  constructor(config: PayrollConfig = DEFAULT_PAYROLL_CONFIG) {
    this.config = config
  }

  /**
   * Run payroll for a given period and set of employees.
   * Returns generated payslips and summary.
   */
  run(input: PayrollRunInput): PayrollRunResult {
    const config = input.config ?? this.config
    const payPeriodsPerYear = this.getPayPeriodsPerYear(input.period.payFrequency)
    const workingDaysInPeriod = this.getWorkingDaysInPeriod(
      input.period.payFrequency,
      input.period.periodStart,
      input.period.periodEnd,
    )

    const payslips: Payslip[] = []
    let totalGrossPay = 0
    let totalDeductions = 0
    let totalNetPay = 0

    for (const employee of input.employees) {
      const payslip = this.computePayslip(
        employee,
        input.period,
        input.attendanceRecords.filter(a => a.employeeId === employee.id),
        input.loansByEmployee.get(employee.id),
        config,
        payPeriodsPerYear,
        workingDaysInPeriod,
      )

      payslips.push(payslip)
      totalGrossPay += payslip.grossPay
      totalDeductions += payslip.totalDeductions
      totalNetPay += payslip.netPay
    }

    const journalEntries: PayrollJournalEntry[] = [
      { accountName: 'Salaries Expense', debit: Math.round(totalGrossPay * 100) / 100, credit: 0 },
      { accountName: 'SSS Payable', debit: 0, credit: Math.round(payslips.reduce((sum, p) => sum + p.sssContribution, 0) * 100) / 100 },
      { accountName: 'PhilHealth Payable', debit: 0, credit: Math.round(payslips.reduce((sum, p) => sum + p.philhealthContribution, 0) * 100) / 100 },
      { accountName: 'Pag-IBIG Payable', debit: 0, credit: Math.round(payslips.reduce((sum, p) => sum + p.pagibigContribution, 0) * 100) / 100 },
      { accountName: 'Withholding Tax Payable', debit: 0, credit: Math.round(payslips.reduce((sum, p) => sum + p.withholdingTax, 0) * 100) / 100 },
    ]

    // Loans and other deductions can be added here if needed, but keeping it to requested ones
    const loansCredit = Math.round(payslips.reduce((sum, p) => sum + p.sssLoan + p.pagibigLoan + p.cashAdvances + p.lateDeduction + p.absenceDeduction + p.otherDeductions, 0) * 100) / 100
    if (loansCredit > 0) {
      journalEntries.push({ accountName: 'Other Deductions Payable', debit: 0, credit: loansCredit })
    }

    journalEntries.push({ accountName: 'Salaries Payable', debit: 0, credit: Math.round(totalNetPay * 100) / 100 })

    return {
      payslips,
      summary: {
        totalGrossPay: Math.round(totalGrossPay * 100) / 100,
        totalDeductions: Math.round(totalDeductions * 100) / 100,
        totalNetPay: Math.round(totalNetPay * 100) / 100,
        employeeCount: input.employees.length,
      },
      journalEntries,
    }
  }

  /**
   * Compute a single payslip for one employee in a pay period.
   */
  private computePayslip(
    employee: Employee,
    period: PayrollRunInput['period'],
    attendance: AttendanceRecord[],
    loans: { sssLoan: number; pagibigLoan: number; cashAdvances: number } | undefined,
    config: PayrollConfig,
    payPeriodsPerYear: number,
    workingDaysInPeriod: number,
  ): Payslip {
    const monthlySalary = employee.basicSalary

    // ─── Compute period-specific salary ────────────────────
    const periodBasicPay = this.computePeriodBasicPay(
      monthlySalary,
      period.payFrequency,
      period.periodStart,
      period.periodEnd,
    )

    // ─── Attendance summary ────────────────────────────────
    const {
      daysWorked,
      hoursWorked,
      overtimeHours,
      lateMinutes,
      absences,
      nightHours,
    } = this.summarizeAttendance(attendance)

    const hourlyRate = ContributionService.getHourlyRate(monthlySalary)
    const dailyRate = ContributionService.getDailyRate(monthlySalary)

    // ─── Overtime Pay ──────────────────────────────────────
    const overtimePay = ContributionService.computeOvertimePay(
      hourlyRate, overtimeHours, config.overtimeRateNormal,
    )

    // ─── Night Differential ────────────────────────────────
    const nightDifferential = ContributionService.computeNightDifferential(
      hourlyRate, nightHours, config,
    )

    // ─── Late / Absence Deductions ─────────────────────────
    const lateDeduction = ContributionService.computeLateDeduction(hourlyRate, lateMinutes)
    const absenceDeduction = absences * dailyRate

    // ─── Government Contributions (employee share) ─────────
    const contributions = ContributionService.computeAllContributions(monthlySalary, config)

    // Scale contributions for semi-monthly (divide by 2)
    const periodDivisor = period.payFrequency === 'semi_monthly' ? 2 : 1
    const sssContribution = Math.round(contributions.sss.employeeShare / periodDivisor * 100) / 100
    const philhealthContribution = Math.round(contributions.philhealth.employeeShare / periodDivisor * 100) / 100
    const pagibigContribution = contributions.pagibig.employeeShare // per period (already per month)

    // ─── Gross Pay ─────────────────────────────────────────
    const allowance = 0 // From employee config or metadata
    const otherEarnings = 0 // From custom fields
    const grossPay = Math.round((
      periodBasicPay + overtimePay + nightDifferential + allowance + otherEarnings
    ) * 100) / 100

    // ─── Withholding Tax ───────────────────────────────────
    const taxableIncome = grossPay - sssContribution - philhealthContribution - pagibigContribution
    const withholdingTax = TaxService.computeMonthlyWithholdingTax(
      taxableIncome,
      payPeriodsPerYear,
    )

    // ─── Loans & Other Deductions ──────────────────────────
    const sssLoan = loans?.sssLoan ?? 0
    const pagibigLoan = loans?.pagibigLoan ?? 0
    const cashAdvances = loans?.cashAdvances ?? 0
    const otherDeductions = 0

    const totalDeductions = Math.round((
      sssContribution + philhealthContribution + pagibigContribution +
      withholdingTax + sssLoan + pagibigLoan + cashAdvances +
      lateDeduction + absenceDeduction + otherDeductions
    ) * 100) / 100

    const netPay = Math.round((grossPay - totalDeductions) * 100) / 100

    // ─── 13th Month Pay (pro-rated) ────────────────────────
    const thirteenthMonthPay = config.thirteenthMonthEnabled
      ? Math.round(monthlySalary / 12 / periodDivisor * 100) / 100
      : 0

    return {
      id: '', // Generated by repository on create
      tenantId: employee.tenantId,
      payrollPeriodId: '', // Set by payroll run
      employeeId: employee.id,
      employeeName: employee.fullName,
      employeeCode: employee.employeeCode,
      department: employee.department,
      position: employee.position,
      basicPay: periodBasicPay,
      overtimePay,
      holidayPay: 0,
      nightDifferential,
      allowance,
      otherEarnings,
      thirteenthMonthPay,
      grossPay,
      sssContribution,
      philhealthContribution,
      pagibigContribution,
      withholdingTax,
      sssLoan,
      pagibigLoan,
      cashAdvances,
      lateDeduction,
      absenceDeduction,
      otherDeductions,
      totalDeductions,
      netPay,
      daysWorked,
      hoursWorked,
      overtimeHours,
      lateMinutes,
      absences,
      periodStart: period.periodStart,
      periodEnd: period.periodEnd,
      payFrequency: period.payFrequency,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      deletedAt: null,
      version: 1,
      createdBy: 'payroll-engine',
      updatedBy: 'payroll-engine',
    }
  }

  /**
   * Compute basic pay for a given period based on pay frequency.
   */
  private computePeriodBasicPay(
    monthlySalary: number,
    frequency: PayFrequency,
    _periodStart: string,
    _periodEnd: string,
  ): number {
    switch (frequency) {
      case 'monthly':
        return monthlySalary
      case 'semi_monthly':
        return Math.round(monthlySalary / 2 * 100) / 100
      case 'weekly':
        return Math.round(monthlySalary * 12 / 52 * 100) / 100
      case 'daily':
        return ContributionService.getDailyRate(monthlySalary)
    }
  }

  /**
   * Summarize attendance records for payroll computation.
   */
  private summarizeAttendance(records: AttendanceRecord[]): {
    daysWorked: number
    hoursWorked: number
    overtimeHours: number
    lateMinutes: number
    absences: number
    nightHours: number
  } {
    const timeInRecords = records.filter(r => r.type === 'time_in')
    const daysWorked = new Set(timeInRecords.map(r => r.attendanceDate)).size
    const hoursWorked = timeInRecords.length * 4 // Rough estimate: 4h per half-day
    const overtimeMinutes = records
      .filter(r => r.type === 'overtime_in' || r.type === 'overtime_out')
      .reduce((s, r) => s + r.overtimeMinutes, 0)
    const lateMinutes = records.reduce((s, r) => s + r.lateMinutes, 0)
    const absences = records.filter(r => r.isUndertime && r.undertimeMinutes > 240).length
    const nightHours = 0 // Computed from attendance time ranges

    return {
      daysWorked,
      hoursWorked,
      overtimeHours: Math.round(overtimeMinutes / 60 * 10) / 10,
      lateMinutes,
      absences,
      nightHours,
    }
  }

  /**
   * Number of pay periods per year for withholding tax computation.
   */
  private getPayPeriodsPerYear(frequency: PayFrequency): number {
    switch (frequency) {
      case 'daily': return 261
      case 'weekly': return 52
      case 'semi_monthly': return 24
      case 'monthly': return 12
    }
  }

  /**
   * Builds a balanced Journal Entry for a completed Payroll Run.
   */
  static buildPayrollJournalEntry(result: PayrollRunResult, tenantId: string): any {
    const totalGrossPay = result.summary.totalGrossPay
    const totalNetPay = result.summary.totalNetPay
    const totalSSS = result.payslips.reduce((sum, p) => sum + p.sssContribution, 0)
    const totalPhilHealth = result.payslips.reduce((sum, p) => sum + p.philhealthContribution, 0)
    const totalPagIBIG = result.payslips.reduce((sum, p) => sum + p.pagibigContribution, 0)
    const totalBIR = result.payslips.reduce((sum, p) => sum + p.withholdingTax, 0)

    const lines = [
      { accountCode: '5010', accountName: 'Salaries Expense', debitAmount: totalGrossPay, creditAmount: 0 },
      { accountCode: '2200', accountName: 'SSS Payable', debitAmount: 0, creditAmount: totalSSS },
      { accountCode: '2210', accountName: 'PhilHealth Payable', debitAmount: 0, creditAmount: totalPhilHealth },
      { accountCode: '2220', accountName: 'Pag-IBIG Payable', debitAmount: 0, creditAmount: totalPagIBIG },
      { accountCode: '2300', accountName: 'BIR Withholding Tax Payable', debitAmount: 0, creditAmount: totalBIR },
      { accountCode: '2010', accountName: 'Salaries Payable', debitAmount: 0, creditAmount: totalNetPay }
    ]

    // Validate balance
    const totalDebit = Math.round(lines.reduce((s, l) => s + (l.debitAmount || 0), 0) * 100) / 100
    const totalCredit = Math.round(lines.reduce((s, l) => s + (l.creditAmount || 0), 0) * 100) / 100

    if (totalDebit !== totalCredit) {
      throw new Error(`Payroll Journal Entry is unbalanced. Debits: ${totalDebit}, Credits: ${totalCredit}`)
    }

    const entry = {
      id: `je-payroll-${Date.now()}`,
      tenantId,
      entryDate: Date.now(),
      referenceNumber: `PR-${Date.now()}`,
      description: `Payroll Run for ${result.summary.employeeCount} employees`,
      entryType: 'auto',
      totalDebit,
      totalCredit,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      deletedAt: null,
      version: 1,
      createdBy: 'payroll-engine',
      updatedBy: 'payroll-engine',
      lines
    }

    return entry
  }

  /**
   * Working days in this pay period (approximate).
   */
  private getWorkingDaysInPeriod(
    frequency: PayFrequency,
    periodStart: string,
    periodEnd: string,
  ): number {
    const start = new Date(periodStart)
    const end = new Date(periodEnd)
    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1

    switch (frequency) {
      case 'semi_monthly': return Math.min(diffDays, 13) // ~13 working days
      case 'monthly': return Math.min(diffDays, 26)
      case 'weekly': return Math.min(diffDays, 6)
      case 'daily': return 1
    }
  }
}
